"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomerSalesOrdersHandler = void 0;
const React = require("react");
const react_1 = require("react");
const CustomerSalesOrdersContext_1 = require("../../contexts/CustomerSalesOrdersContext");
const useSyncedData_1 = require("../../../../../hooks/useSyncedData");
const useClient_1 = require("../../../../../hooks/useClient");
const salesOrder_1 = require("../../types/salesOrder");
const useFetchRetailCustomerSalesOrders_1 = require("../../hooks/useFetchRetailCustomerSalesOrders");
const useTickReRender_1 = require("../../../../../hooks/useTickReRender");
const CustomerSalesOrdersHandler = ({ children }) => {
    const client = (0, useClient_1.useClient)();
    const tick = (0, useTickReRender_1.useTickReRender)();
    const retailCustomerId = (0, react_1.useMemo)(() => client.auth.session &&
        client.auth.session.retailCustomer &&
        client.auth.session.retailCustomer.retailCustomerId, [client.auth.session]);
    const rawOutstandingSalesOrders = (0, useSyncedData_1.useSyncedData)(client.retailSalesOrders.retrieveSyncedRetailCustomerSalesOrders(retailCustomerId, salesOrder_1.TCustomerSalesOrder.Outstanding));
    const outstandingSalesOrders = (0, react_1.useMemo)(() => {
        if (rawOutstandingSalesOrders === undefined) {
            return null;
        }
        const dueOutstandingOrders = rawOutstandingSalesOrders.filter((salesOrder) => salesOrder.predictedCompletionTime >= tick);
        const sortedDueOutstandingOrders = dueOutstandingOrders.sort((a, b) => a.predictedCompletionTime - b.predictedCompletionTime);
        const overDueOutstandingOrders = rawOutstandingSalesOrders.filter((salesOrder) => salesOrder.predictedCompletionTime < tick);
        const sortedOverdueOutstandingOrders = overDueOutstandingOrders.sort((a, b) => b.predictedCompletionTime - a.predictedCompletionTime);
        return sortedDueOutstandingOrders.concat(sortedOverdueOutstandingOrders);
    }, [rawOutstandingSalesOrders, tick]);
    const [pastSalesOrdersPageNumber, setPastSalesOrdersPageNumber] = (0, react_1.useState)(null);
    const [pastSalesOrders, setPastSalesOrders] = (0, react_1.useState)(null);
    const updatedPastSalesOrders = (0, useSyncedData_1.useSyncedData)(client.retailSalesOrders.retrieveSyncedRetailCustomerSalesOrders(retailCustomerId, salesOrder_1.TCustomerSalesOrder.Past, {
        page: 1
    }));
    // first set of past sales orders have either been completed or refunded so we update the view
    (0, react_1.useEffect)(() => {
        if (updatedPastSalesOrders === undefined) {
            return;
        }
        setPastSalesOrdersPageNumber(null);
    }, [updatedPastSalesOrders]);
    const { refetch: fetchPastSalesOrders, isLoading: isLoadingPastSalesOrdersFetch, error: errorPastSalesOrdersFetch } = (0, useFetchRetailCustomerSalesOrders_1.useFetchRetailCustomerSalesOrders)(retailCustomerId, salesOrder_1.TCustomerSalesOrder.Past, {
        page: pastSalesOrdersPageNumber
    });
    (0, react_1.useEffect)(() => {
        if (retailCustomerId === null) {
            // no customer so reset everything
            setPastSalesOrders(null);
            setPastSalesOrdersPageNumber(null);
            return;
        }
        // customer has loaded / changed so we reset the past orders and page
        setPastSalesOrders([]);
        setPastSalesOrdersPageNumber(1);
    }, [retailCustomerId]);
    (0, react_1.useEffect)(() => {
        if (retailCustomerId === null) {
            return;
        }
        if (pastSalesOrdersPageNumber === null) {
            setPastSalesOrdersPageNumber(1);
            return;
        }
        fetchPastSalesOrders().then((data) => {
            // type check because data can come back void
            if (!Array.isArray(data)) {
                return;
            }
            const allPastSalesOrders = pastSalesOrdersPageNumber === 1 ? data : pastSalesOrders.concat(data);
            setPastSalesOrders(allPastSalesOrders);
        });
    }, [pastSalesOrdersPageNumber]);
    // provide a reference to the previous count of sales orders before load
    const previousPastSalesOrdersCountRef = (0, react_1.useRef)();
    (0, react_1.useEffect)(() => {
        previousPastSalesOrdersCountRef.current = Array.isArray(pastSalesOrders)
            ? pastSalesOrders.length
            : 0;
    });
    const previousPastSalesOrdersCount = previousPastSalesOrdersCountRef.current;
    const loadMorePastSalesOrders = (0, react_1.useCallback)(() => {
        if (errorPastSalesOrdersFetch instanceof Error) {
            setPastSalesOrdersPageNumber(null);
            return;
        }
        if (pastSalesOrders === null || isLoadingPastSalesOrdersFetch) {
            return;
        }
        // if we haven't loaded any more then stop updating
        if (previousPastSalesOrdersCount === pastSalesOrders.length &&
            pastSalesOrdersPageNumber > 1) {
            return;
        }
        if (pastSalesOrders.length === 0) {
            // past orders are empty so next fetch should be the first set
            setPastSalesOrdersPageNumber(1);
        }
        else if (errorPastSalesOrdersFetch !== null) {
            // if an error occurs we re-fetch the same page
            setPastSalesOrdersPageNumber(pastSalesOrdersPageNumber);
        }
        else {
            // fetch the next page if orders are present
            setPastSalesOrdersPageNumber(pastSalesOrdersPageNumber + 1);
        }
    }, [
        pastSalesOrders,
        isLoadingPastSalesOrdersFetch,
        previousPastSalesOrdersCount,
        errorPastSalesOrdersFetch,
        setPastSalesOrdersPageNumber,
        pastSalesOrdersPageNumber
    ]);
    // provide a reference to the previous outstanding sales orders
    const previousOutstandingSalesOrdersRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        previousOutstandingSalesOrdersRef.current = outstandingSalesOrders;
    });
    const previousOutstandingSalesOrders = previousOutstandingSalesOrdersRef.current;
    // an order is completed when it disappears from the current outstanding
    // orders from the previous
    // when this happens we refresh the past sales order
    (0, react_1.useEffect)(() => {
        if (outstandingSalesOrders === null ||
            previousOutstandingSalesOrders === null) {
            return;
        }
        const completedOrders = previousOutstandingSalesOrders.filter((previousOutstandingSalesOrder) => !outstandingSalesOrders.some((currentOutstandingSalesOrder) => previousOutstandingSalesOrder.salesOrderId ===
            currentOutstandingSalesOrder.salesOrderId));
        if (completedOrders.length > 0) {
            setPastSalesOrdersPageNumber(1);
        }
    }, [outstandingSalesOrders, previousOutstandingSalesOrders]);
    const allSalesOrders = (0, react_1.useMemo)(() => {
        if (outstandingSalesOrders === null || pastSalesOrders === null) {
            return null;
        }
        return outstandingSalesOrders.concat(pastSalesOrders);
    }, [outstandingSalesOrders, pastSalesOrders]);
    const contextValue = (0, react_1.useMemo)(() => ({
        outstandingSalesOrders,
        pastSalesOrdersPageNumber,
        setPastSalesOrdersPageNumber,
        pastSalesOrders,
        isLoadingPastSalesOrdersFetch,
        errorPastSalesOrdersFetch,
        loadMorePastSalesOrders,
        allSalesOrders
    }), [
        outstandingSalesOrders,
        pastSalesOrdersPageNumber,
        setPastSalesOrdersPageNumber,
        pastSalesOrders,
        isLoadingPastSalesOrdersFetch,
        errorPastSalesOrdersFetch,
        loadMorePastSalesOrders,
        allSalesOrders
    ]);
    return (React.createElement(CustomerSalesOrdersContext_1.CustomerSalesOrdersContext.Provider, { value: contextValue }, children));
};
exports.CustomerSalesOrdersHandler = CustomerSalesOrdersHandler;
//# sourceMappingURL=index.js.map